import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowDownstreamDatasourcesFilteredByFlowOutputFieldQuery,
  FlowDownstreamDatasourcesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamDatasourcesConnection_nodes,
  FlowDownstreamDatasourcesFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamDatasourcesFilteredByFlowOutputFieldQuery';
import {
  FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery,
  // tslint:disable-next-line: max-line-length
  FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_downstreamDatasourcesConnection_nodes,
  FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery';
import {
  FlowDownstreamDatasourcesQuery,
  FlowDownstreamDatasourcesQuery_flows_downstreamDatasourcesConnection_nodes,
  FlowDownstreamDatasourcesQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamDatasourcesQuery';
import {
  FlowDownstreamDatasourcesWithoutPersonalSpaceQuery,
  FlowDownstreamDatasourcesWithoutPersonalSpaceQuery_flows_downstreamDatasourcesConnection_nodes,
  FlowDownstreamDatasourcesWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamDatasourcesWithoutPersonalSpaceQuery';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import { FlowDownstreamDatasourcesFilteredByFlowOutputFieldQueryGql } from '../../queries/FlowDownstreamDatasourcesFilteredByFlowOutputFieldQueryGql';
import { FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql } from '../../queries/FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql';
import { FlowDownstreamDatasourcesQueryGql } from '../../queries/FlowDownstreamDatasourcesQueryGql';
import { FlowDownstreamDatasourcesWithoutPersonalSpaceQueryGql } from '../../queries/FlowDownstreamDatasourcesWithoutPersonalSpaceQueryGql';
import { DatasourcesDisplayGrid, DatasourcesDisplayGridRowData } from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = FlowDownstreamDatasourcesQueryVariables;
type QueryResults = FlowDownstreamDatasourcesQuery;
type QueryResultListNode = FlowDownstreamDatasourcesQuery_flows_downstreamDatasourcesConnection_nodes;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = FlowDownstreamDatasourcesWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = FlowDownstreamDatasourcesWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace =
  FlowDownstreamDatasourcesWithoutPersonalSpaceQuery_flows_downstreamDatasourcesConnection_nodes;

type QueryVariablesWithFilter = FlowDownstreamDatasourcesFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowDownstreamDatasourcesFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowDownstreamDatasourcesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamDatasourcesConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery;
// tslint:disable-next-line: max-line-length
type QueryResultListNodeWithFilterWithoutPersonalSpace = FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_downstreamDatasourcesConnection_nodes;


function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamDatasourcesConnection : null;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamDatasourcesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamDatasourcesConnection : null;
}

function selectConnectionListResultForFilteredWithoutPersonalSpaceQuery(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamDatasourcesConnection : null;
}

function mapResultNodeWithoutPersonalSpace(node: QueryResultListNodeWithoutPersonalSpace): GridRowData {
  return {
    id: node.id,
    name: node.name,
    description: node.description || '',
    owner: node.owner,
    workbookCount: node.downstreamWorkbooksConnection ? node.downstreamWorkbooksConnection.totalCount : null,
    vizportalUrlId: node.vizportalUrlId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    extractLastUpdateTime: node.extractLastUpdateTime || null,
    containerName: node.projectName,
    containerType: 'project',
    projectVizportalUrlId: node.projectVizportalUrlId,
    hasExtracts: !!node.hasExtracts,
    isCertified: node.isCertified,
    certifier: node.certifier,
    certificationNote: node.certificationNote || ''
  };
}

function mapResultNode(node: QueryResultListNode): GridRowData {
  return {
    id: node.id,
    name: node.name,
    description: node.description || '',
    owner: node.owner,
    workbookCount: node.downstreamWorkbooksConnection ? node.downstreamWorkbooksConnection.totalCount : null,
    vizportalUrlId: node.vizportalUrlId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    extractLastUpdateTime: node.extractLastUpdateTime || null,
    containerName: node.containerName,
    containerType: node.containerType,
    projectVizportalUrlId: node.projectVizportalUrlId,
    hasExtracts: !!node.hasExtracts,
    isCertified: node.isCertified,
    certifier: node.certifier,
    certificationNote: node.certificationNote || ''
  };
}

export type FlowDownstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowDownstreamDatasourcesQueryGrid(props: FlowDownstreamDatasourcesQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDatasourcesLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
        queryGql={FlowDownstreamDatasourcesFilteredByFlowOutputFieldQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={mapResultNode}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={FlowDownstreamDatasourcesQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={mapResultNode}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithFilterWithoutPersonalSpace, GridRowData>
        queryGql={FlowDownstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredWithoutPersonalSpaceQuery}
        mapResultNode={mapResultNodeWithoutPersonalSpace}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={FlowDownstreamDatasourcesWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={mapResultNodeWithoutPersonalSpace}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  );
}
